import { Component, Inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { Auth0SessionService } from 'src/app/shared/services/api/auth0-session.service';
import { MaritzService } from 'src/app/shared/services/api/maritz.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { WalletSetupService } from 'src/app/shared/services/api/wallet-setup.service';
import { WalletSummaryService } from 'src/app/shared/services/api/wallet-summary.service';
import { WalletSummary } from 'src/app/shared/models/wallet-summary.interface';
import { TransactionHistoryService } from 'src/app/shared/services/api/transaction-history.service';
import { TransactionHistory } from 'src/app/shared/models/transaction-history.interface';
import { filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { TealiumLoaderService } from 'src/app/shared/services/tealium-loader.service';

interface FaqItem {
  question: string;
  answer: string;
  isOpen: boolean;
}

interface ActivityItem {
  title: string;
  id: string;
  date: string;
  rawDate: string;
  points: number;
  type: 'earned' | 'redeemed';
}

@Component({
  selector: 'app-next-gen-home',
  templateUrl: './next-gen-home.component.html',
  styleUrls: ['./next-gen-home.component.scss']
})

export class NextGenHomeComponent implements OnInit {

  isPageLoading: boolean = true;
  faqItems: FaqItem[] = [];
  showTncModal = false;
  showUnenrollModal = false;
  isFaqExpanded = false;

  pointsAvailable: string = '0';
  pointsEarned: string = '0';
  pointsRedeemed: string = '0';
  allActivity: ActivityItem[] = [];

  companyName: string = '...';
  showMaritzError: boolean = false;

  constructor(
      private http: HttpClient,
      private auth: AuthService,
      private auth0Session: Auth0SessionService,
      private maritzService: MaritzService,
      private sessionService: SessionService,
      private router: Router,
      private walletSetupService: WalletSetupService,
      private walletSummaryService: WalletSummaryService,
      private transactionHistoryService: TransactionHistoryService,
      private tealiumService: TealiumLoaderService,
      @Inject(DOCUMENT) private document: Document
      ) {}

  ngOnInit(): void {
    console.log('1. ngOnInit Started');
    this.loadFaqData();
    this.initAuthSession();

    this.tealiumService.loadTealium().then(() => {
      // Once loaded, notify Adobe Target for the Home dashboard view
      this.tealiumService.notifyViewChange('rewards_account_dashboard', true);
      this.tealiumService.trackAction({
        page_id: 'rewards_account_dashboard',
        page_name: 'Rewards Account Dashboard',
        site_area: 'Rewards_Dashboard',
        site_sub_area: 'Rewards_Dashboard'
      }, 'view');
    }).catch(err => {
      console.warn('[NextGenHome] Tealium initialization bypassed due to loading error', err);
    });

  }

  loadDashboardData() {
    // Retrieve ID from storage (Handle the JSON string format)
    const rawWalletId = sessionStorage.getItem('WalletID');
    const walletId = rawWalletId ? JSON.parse(rawWalletId) : '';
    
    // Default role to 'admin' if missing, or grab from storage
    const walletRole = 'admin'; 

    if (walletId) {
      this.walletSummaryService.fetchWalletSummary(walletId, walletRole).subscribe({
        next: (data: WalletSummary) => {
          console.log('✅ Dashboard Data Loaded:', data);

          // 3. Extract Data from 'upsdotcom' section
          if (data.upsdotcom && data.upsdotcom.wallet && data.upsdotcom.wallet.rewards) {
             const rewards = data.upsdotcom.wallet.rewards;
             
             // Assign to variables
             this.pointsAvailable = rewards.totalRewardBalance || '0';
             this.pointsEarned = rewards.totalRewardsEarned || '0';
             this.pointsRedeemed = rewards.totalRewardRedeemed || '0';
          }
        },
        error: (err) => console.error('❌ Failed to load dashboard data', err)
      });
    }
  }

loadHistoryData() {
    const rawWalletId = sessionStorage.getItem('WalletID');
    const walletId = rawWalletId ? JSON.parse(rawWalletId) : '';
    const walletRole = 'admin'; 

    if (walletId) {
      this.transactionHistoryService.fetchTransactionHistory(walletId, walletRole, 'all')
        .subscribe({
          next: (data: TransactionHistory) => {
            console.log('📜 Transaction History API:', data);
            
            const rewards = data.walletTransactionsList?.rewards;
            if (!rewards) return;

            const mappedData: ActivityItem[] = [];

            // --- 1. Map EARNED Points ---
            if (rewards.rewardsEarn) {
              rewards.rewardsEarn.forEach(item => {
                const category = (item.earnCategory || '').toLowerCase();
                const tabType: 'earned' | 'redeemed' = category === 'redemption' ? 'redeemed' : 'earned';

                mappedData.push({
                  title: item.earnIdentifier || item.earnCategory || 'Points Earned',
                  id: item.transactionID || 'N/A',
                  date: this.formatDate(item.earnDate), // The pretty date for the UI
                  rawDate: item.earnDate,               // <-- ADDED: The raw timestamp for sorting
                  points: Math.abs(parseFloat(item.pointsEarned)), 
                  type: tabType 
                });
              });
            }

            // --- 2. Map SPENT Points (Redeemed) ---
          if (rewards.rewardsSpends) {
            rewards.rewardsSpends.forEach(item => {
              const category = (item.spendCategory || '').toLowerCase();
              const tabType: 'earned' | 'redeemed' = category === 'revenue' ? 'earned' : 'redeemed';

              // Safely access properties using an untyped wrapper
              const rawItem = item as any;
              const invoiceNum = rawItem.invoiceNumber || rawItem.invoiceID || rawItem.sourceSystemID1;

              let displayTitle = item.spendIdentifier || item.spendCategory || 'Redemption';
              
              if (invoiceNum) {
                displayTitle = `UPS Invoice Number: ${invoiceNum}`;
              }

              mappedData.push({
                title: displayTitle,
                id: item.transactionID || 'N/A',
                date: this.formatDate(item.spendDate),
                rawDate: item.spendDate,
                points: -Math.abs(parseFloat(item.pointSpend)), 
                type: tabType 
              });
            });
          }

            // --- 3. Sort by RAW Date (Newest First) ---
            // Notice we are using b.rawDate and a.rawDate now!
            this.allActivity = mappedData.sort((a, b) => 
               new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime()
            );
            
            console.log('✅ Final Mapped Activity:', this.allActivity);
          },
          error: (err) => console.error('❌ History Load Error:', err)
        });
    }
  }

  // Helper to format date string to "Feb 11, 2026"
  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  

checkTnCStatus() {
    // Read the value we just saved in SessionService
    const tcStatus = sessionStorage.getItem('TCAcceptance');
    
    console.log('📝 Checking T&C Status:', tcStatus);

    // Logic: If status is '0' (Not Accepted) or missing, SHOW the modal.
    if (tcStatus === '0' || !tcStatus) {
      this.showTncModal = true; 
    } else {
      this.showTncModal = false;
    }
  }

  handleTncAccept() {
    console.log('User clicked Accept. Updating backend...');

    // 1. Retrieve Data from Session Storage
    // Note: WalletID was stored as a JSON string, so we parse it to remove extra quotes if needed
    const rawWalletId = sessionStorage.getItem('WalletID');
    const cleanWalletId = rawWalletId ? JSON.parse(rawWalletId) : '';
    const sessionId = sessionStorage.getItem('SessionID');

    // 2. Construct Payload (Matches the format you provided)
    const walletSetupData = {
      walletId: cleanWalletId,
      sessionID: sessionId,
      // You might need to adjust these hardcoded values based on your backend requirements for NextGen
      productType: 'UPSdotcom', 
      productSourceSystem: 'UPSdotcom' 
    };

    // 3. Call the API
    // ✅ CORRECT (This calls the new Azure API)
this.walletSetupService.updateWalletSetupNextGen(walletSetupData).subscribe({
      next: (response) => {
        console.log('✅ Backend updated successfully! T&C Accepted.', response);
        
        // 4. Update Local State
        this.showTncModal = false;
        
        // Update the session storage flag so it doesn't reappear on refresh
        sessionStorage.setItem('TCAcceptance', '1'); 
        this.isPageLoading = true;

        console.log('⚡ Refreshing Dashboard Data after T&C Acceptance...');
            setTimeout(() => {
            this.loadDashboardData(); 
            this.loadHistoryData();
            
            // Turn off the spinner (Ideally, you do this inside the subscribe of your load methods, 
            // but doing it here works for a quick fix).
            this.isPageLoading = false; 
        }, 1500);

//        this.loadDashboardData(); 
 //       this.loadHistoryData();
      },
      error: (error) => {
        console.error('❌ Failed to update T&C acceptance on backend:', error);
        // Optional: Decide if you want to close the modal anyway or show an error
        // this.showTncModal = false; 
      }
    });
  }

handleLogout(autoLogin: boolean = true) {
    console.log('🚪 Logging out...');

    // 1. CRITICAL: Clear all session data. 
    sessionStorage.clear();
    
    // 2. Set a flag so the login page knows to instantly redirect
    localStorage.removeItem('triggerAutoLogin');
    
    // 3. Perform Auth0 Logout
    this.auth.logout({ 
      logoutParams: { 
        returnTo: this.document.location.origin + '/next-gen-login'
      }
    });
  }

  openUnenrollModal() {
    this.showUnenrollModal = true;
  }

  closeUnenrollModal() {
    this.showUnenrollModal = false;
  }

handleTncDecline() {
    console.log('User declined T&C. initiating Logout and redirecting to UPS.com...');

    // 1. Clear session data
    sessionStorage.clear();

    // 2. ✅ Drop a breadcrumb for the login page to read
    localStorage.setItem('redirectToUps', 'true');

    // 3. Perform a safe Auth0 Logout to the whitelisted URL
    this.auth.logout({ 
      logoutParams: { 
        returnTo: this.document.location.origin + '/next-gen-login'
      }
    });
  }

  handleUnenrollConfirm() {
    console.log('User confirmed unenrollment');
    // TODO: Call your backend API to delete the account here
    this.showUnenrollModal = false;
    // Optional: Redirect to login or show success message
   // this.handleLogout(); 
   this.handleLogout(false);
  }

initAuthSession() {
    console.log('2. initAuthSession called. Waiting for Auth0...');

    // DEBUGGING THE WAIT LOGIC
    this.auth.isLoading$.subscribe(loading => console.log('   -> Auth0 Loading State:', loading));
    this.auth.isAuthenticated$.subscribe(auth => console.log('   -> Auth0 Authenticated State:', auth));

    this.auth.isLoading$.pipe(
      filter(isLoading => !isLoading), 
      switchMap(() => this.auth.isAuthenticated$),
      filter(isAuthenticated => isAuthenticated),
      // 🟢 NEW: Switch over to the user profile to extract the token payload!
      switchMap(() => this.auth.user$) ,
      take(1)
    ).subscribe({
      next: (user) => {
        console.log('3. Auth0 is Ready & User is Logged In! Calling Backend now...');
        
        // 🟢 NEW: Extract the string and pass it to your function
        const imsValue = user ? user['https://identity.ups.com/imsCookieValue'] : '';
        
        if (!imsValue) {
           console.warn('⚠️ IMS Cookie Value missing from Auth0 token!');
        }

        // Pass the value into the authorization function
        this.callBackendAuthorization(imsValue); 
      },
      error: (err) => console.error('Auth Stream Error:', err),
      complete: () => console.log('Auth Stream Completed (Unexpected)')
    });
  }

  callBackendAuthorization(imsValue: string) {
    console.log('🚀 Triggering Backend Authorization...');
    
    // The Interceptor will automatically add the Authorization Header
    this.auth0Session.authorizeSession(imsValue).subscribe({
      next: (response) => {
        console.log('✅ Backend Success! Session Authorized:', response);

        // Pass data to service to save to SessionStorage
        this.sessionService.handleNextGenSession(response);

        // 🛑 NEW KICK-OUT LOGIC: Check status before doing ANYTHING else
        const rawStatus = sessionStorage.getItem('WalletStatus') || response.status || response.walletStatus || '';
        const currentStatus = rawStatus.toLowerCase();

        if (
          currentStatus === 'deleted' || 
          currentStatus === 'inactive' ||
          response.success === false || 
          response.message === 'WalletID not found.'
        ) {
          console.warn('❌ User is unenrolled. Logging out and redirecting to error...');
          
          sessionStorage.clear();
          localStorage.clear();

          // Log out of Auth0 and append the custom error flag to the redirect URL
          this.auth.logout({ 
            logoutParams: { 
              returnTo: this.document.location.origin + '/next-gen-login?error=account_deleted'
            }
          });
          
          return; // 🛑 CRITICAL: This stops the code here so the T&C modal NEVER loads!
        }

        this.isPageLoading = false;

        // ✅ IF ACTIVE, PROCEED NORMALLY
        this.companyName = response.companyName || response.displayName || response.userFirstName || 'User';

        // 🛑 NEW LOGIC: Check T&C status BEFORE loading any data
        const tcStatus = sessionStorage.getItem('TCAcceptance');

        // console.log('⚡ Loading History Data...');
        // this.loadHistoryData(); 
        // console.log('⚡ Session Hydrated. Now loading Dashboard Data...');
        // this.loadDashboardData();
        // // NOW check if we need to show the modal (After data is saved)
        // this.checkTnCStatus();

        if (tcStatus === '0' || !tcStatus) {
          // If NOT accepted: Just show the modal. Do NOT load data yet.
          console.log('📝 T&C not accepted yet. Showing modal. Waiting to load data...');
          this.showTncModal = true;
        } else {
          // If ALREADY accepted: Proceed with loading all the user's data!
          console.log('✅ T&C already accepted. Loading Dashboard and History...');
          this.showTncModal = false;
          this.loadDashboardData();
          this.loadHistoryData();
        }
      },
      error: (error) => {
        console.error('❌ Backend Hard Failure (User completely unenrolled):', error);

        // 1. Clear out the session
        sessionStorage.clear();
        localStorage.clear();

        // 2. Redirect them to the Login page to show the error toast
        this.auth.logout({ 
          logoutParams: { 
            returnTo: this.document.location.origin + '/next-gen-login?error=account_deleted'
          }
        });
      }
    });
  }

  loadFaqData() {
    // This path works because Angular serves 'src/assets' at '/assets'
    this.http.get<FaqItem[]>('assets/json/faq-rewards.json')
      .subscribe({
        next: (data) => {
          this.faqItems = data;
          
          // Optional: Keep First Item Open
           if (this.faqItems.length > 0) this.faqItems[0].isOpen = true;
        },
        error: (err) => {
          console.error('Failed to load FAQ data', err);
        }
      });
  }

  redirectToMaritz(targetCatalog: string = 'BrowseFullCatalog') {
    console.log('🚀 Initiating Maritz Redirect...');
    console.log(`🚀 Clicked button! Target Path is: [${targetCatalog}]`);
    this.isPageLoading = true; // ✅ Turn on the spinner so the user knows something is happening!

    this.showMaritzError = false;

    const rawWalletId = sessionStorage.getItem('WalletID');
    const walletId = rawWalletId.replace(/"/g, '');
    const sessionId = sessionStorage.getItem('SessionID') || '';

    console.log(`🔍 Extracted Data -> Wallet: [${walletId}], Session: [${sessionId}]`);

    this.maritzService.getMaritzUrl(walletId, sessionId, targetCatalog).subscribe({
      next: (response) => {
        if (response.code === '200' && response.message === 'Success') {
          console.log('✅ Maritz Data received, building form...');
          this.buildAndSubmitMaritzForm(response);
        } else {
          console.error('❌ Maritz API returned an error:', response);
          this.isPageLoading = false; // Turn off spinner if it fails
          this.showMaritzError = true; // Show error message
        }
      },
      error: (error) => {
        console.error('❌ Failed to call Maritz API:', error);
        this.isPageLoading = false;
        this.showMaritzError = true; // Show error message
      }
    });
  }

  // This dynamically creates the HTML form and submits it behind the scenes
  private buildAndSubmitMaritzForm(data: any) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = data.formPostUrl; 

    // Helper function to create hidden inputs
    const addHiddenInput = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    // Map the API response to the exact HTML input names required by Maritz
    addHiddenInput('clientProjectKey', data.clientProjectKey);
    addHiddenInput('ClientIdentifier', data.clientIdentifier);
    addHiddenInput('PartnerIdentifier', data.partnerIdentifier);
    // Notice the API returns "token" but the form requires "redirectToken"
    addHiddenInput('redirectToken', data.token); 

    // Append the form to the document body and instantly submit it
    document.body.appendChild(form);
    form.submit(); 
    
    // Note: We leave this.isPageLoading = true because the browser is now navigating away!
  }

  toggleFaqView() {
    this.isFaqExpanded = !this.isFaqExpanded;
  }

  isExpanded = false;

  get earnedActivity() {
    return this.allActivity.filter(item => item.type === 'earned');
  }

  get redeemedActivity() {
    return this.allActivity.filter(item => item.type === 'redeemed');
  }

  toggleView() {
    this.isExpanded = !this.isExpanded;
  }

  onRewardAppliedSuccess() {
  console.log('🔄 Payment completed! Refreshing transaction history and dashboard summary...');
  this.loadHistoryData();
  this.loadDashboardData();
}

}
